import { Check, X, Minus } from "lucide-react";

const steps = [
    {
        num: "01",
        title: "Analisi dei flussi",
        text: "Partiamo da come lavora davvero il team: reception, tecnici, commerciale, amministrazione e direzione.",
    },
    {
        num: "02",
        title: "Modellazione dei dati",
        text: "Clienti, contratti, rapportini, servizi, licenze e dispositivi vengono collegati in modo coerente.",
    },
    {
        num: "03",
        title: "Automazione operativa",
        text: "Riduciamo attività ripetitive, errori manuali e dispersione delle informazioni.",
    },
    {
        num: "04",
        title: "Dashboard e controllo",
        text: "I dati diventano visibili con KPI, filtri, storico, log e report.",
    },
    {
        num: "05",
        title: "Evoluzione continua",
        text: "TecSuite cresce modulo dopo modulo, seguendo le esigenze reali dell'azienda.",
    },
];

const compareRows = [
    { label: "Aderenza ai processi IT", excel: "no", standard: "parziale", tec: "si" },
    { label: "Tracciabilità", excel: "no", standard: "parziale", tec: "si" },
    { label: "Portale cliente", excel: "no", standard: "no", tec: "si" },
    { label: "Contratti MSP", excel: "no", standard: "parziale", tec: "si" },
    { label: "Rapportini e interventi", excel: "parziale", standard: "parziale", tec: "si" },
    { label: "Evoluzione custom", excel: "no", standard: "no", tec: "si" },
    { label: "Visione unica del cliente", excel: "no", standard: "parziale", tec: "si" },
];

const Cell = ({ state }) => {
    if (state === "si")
        return (
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-teal-100 text-teal-700 ring-1 ring-teal-200">
                <Check className="w-4 h-4" strokeWidth={3} />
            </span>
        );
    if (state === "no")
        return (
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-rose-50 text-rose-500 ring-1 ring-rose-200">
                <X className="w-4 h-4" strokeWidth={3} />
            </span>
        );
    return (
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200">
            <Minus className="w-4 h-4" strokeWidth={3} />
        </span>
    );
};

export default function MethodComparison() {
    return (
        <>
            {/* METHOD */}
            <section
                id="metodo"
                data-testid="method-section"
                className="relative bg-slate-50 py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                            <span className="h-px w-8 bg-teal-500" />
                            Metodo
                        </span>
                        <h2
                            data-testid="method-title"
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                        >
                            Dal processo reale al software che lo governa.
                        </h2>
                    </div>

                    <ol
                        data-testid="method-timeline"
                        className="mt-16 relative grid gap-8 lg:grid-cols-5 lg:gap-6"
                    >
                        <div
                            className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent"
                            aria-hidden
                        />
                        {steps.map((s) => (
                            <li
                                key={s.num}
                                data-testid={`method-step-${s.num}`}
                                className="reveal relative"
                            >
                                <div className="flex lg:flex-col items-start gap-4">
                                    <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm shrink-0">
                                        <span className="font-display text-2xl font-extrabold bg-gradient-to-br from-cyan-600 to-teal-500 bg-clip-text text-transparent">
                                            {s.num}
                                        </span>
                                        <span className="absolute -top-1.5 -right-1.5 h-3 w-3 rounded-full bg-teal-400 ring-4 ring-slate-50" />
                                    </span>
                                    <div>
                                        <h3 className="font-display text-lg font-semibold text-slate-900">
                                            {s.title}
                                        </h3>
                                        <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                                            {s.text}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* COMPARISON */}
            <section
                id="perche"
                data-testid="comparison-section"
                className="relative bg-white py-24 sm:py-28 lg:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">
                            <span className="h-px w-8 bg-cyan-500" />
                            Perché TecSuite
                        </span>
                        <h2
                            data-testid="comparison-title"
                            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
                        >
                            Un confronto onesto tra approcci diversi.
                        </h2>
                        <p className="mt-5 text-lg text-slate-600">
                            TecSuite non è l&apos;unica opzione: è quella pensata per chi lavora nell&apos;IT ogni
                            giorno e vuole processi ordinati, dati collegati e visione unica del cliente.
                        </p>
                    </div>

                    <div
                        data-testid="comparison-table"
                        className="mt-14 overflow-hidden rounded-3xl ring-1 ring-slate-200 bg-white shadow-sm"
                    >
                        {/* Header */}
                        <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200">
                            <div className="px-4 sm:px-6 py-5 text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">
                                Caratteristica
                            </div>
                            <div className="px-4 sm:px-6 py-5 text-center text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">
                                Fogli Excel
                            </div>
                            <div className="px-4 sm:px-6 py-5 text-center text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">
                                Gestionale standard
                            </div>
                            <div className="px-4 sm:px-6 py-5 text-center text-[11px] sm:text-xs font-bold uppercase tracking-widest bg-slate-900 text-cyan-300 relative">
                                TecSuite
                                <span className="absolute inset-x-4 bottom-0 h-[3px] bg-gradient-to-r from-cyan-400 to-teal-400" />
                            </div>
                        </div>
                        {compareRows.map((r, i) => (
                            <div
                                key={r.label}
                                data-testid={`comparison-row-${i}`}
                                className={`grid grid-cols-4 items-center ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}
                            >
                                <div className="px-4 sm:px-6 py-5 text-sm font-medium text-slate-800">
                                    {r.label}
                                </div>
                                <div className="px-4 sm:px-6 py-5 flex justify-center">
                                    <Cell state={r.excel} />
                                </div>
                                <div className="px-4 sm:px-6 py-5 flex justify-center">
                                    <Cell state={r.standard} />
                                </div>
                                <div className="px-4 sm:px-6 py-5 flex justify-center bg-slate-900/[0.02]">
                                    <Cell state={r.tec} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
